import { createRoute, redirect } from '@tanstack/react-router';
import { defaultSiteEnv, loadedSite, loadSessionUser, useEnvStore } from '../../lib/session';
import {
  GettingReadyRoute,
  LogoutRoute,
  NoTeamRoute,
  PartnerOverviewRoute,
  SettingsPasswordRoute,
  SwitchAccountRoute,
} from '../common';
import { authedChromeRoute, authedRoute } from '../layouts';

// Authenticated pages that are not inside the site-env layout.

// The `/` landing route. Names no site, so the destination is resolved here once the user is known.
export const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  async beforeLoad({ location }) {
    const user = await loadSessionUser();
    if (!user) return;
    if (user.currentTeam?.is_partner) {
      throw redirect({
        to: '/$siteId/partners',
        params: { siteId: user.currentTeam.slugged_name },
        search: location.search,
        hash: location.hash,
      });
    }
    const currentTeam = user.currentTeam;
    if (!currentTeam) return;
    // A site already loaded wins, so landing here mid-session keeps the site the user was on. A
    // failed load doesn't count.
    const loaded = loadedSite();
    const siteId = loaded?.siteId ?? currentTeam.slugged_name;
    const siteEnv = loaded?.siteEnv ?? defaultSiteEnv(user, siteId) ?? 'live';
    throw redirect({
      to: '/$siteId/$siteEnv',
      params: { siteId, siteEnv },
      search: location.search,
      hash: location.hash,
    });
  },
});

/**
 * `/$siteId` on its own - the "switch to this site" url. The session guards have already loaded
 * the site named in the path, so hand over to `/`, which lands on the site now loaded (or on the
 * partner portal when the user's own team is a partner).
 */
export const teamRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/$siteId',
  beforeLoad() {
    throw redirect({ to: '/' });
  },
});

/**
 * The 404. It has to be a `beforeLoad` redirect rather than the root's `notFoundComponent`: a
 * component only runs once React renders, so a path the router rejects during matching would
 * otherwise sit on the pending screen.
 */
export const catchAllRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/$',
  beforeLoad() {
    throw redirect({ to: '/' });
  },
});

export const logoutRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/logout',
  component: LogoutRoute,
  staticData: { title: 'auth.logout' },
});

export const noTeamRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/no-team',
  component: NoTeamRoute,
  staticData: { title: 'auth.no_team' },
});

export const gettingReadyRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/getting-ready',
  component: GettingReadyRoute,
});

export const switchAccountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/switch-account/$teamId',
  component: SwitchAccountRoute,
  staticData: { title: 'auth.switch_account' },
});

/**
 * Only partner teams have a partner portal. For anyone else the site in the url is a normal store,
 * so send them to its overview in whichever environment it defaults to - `resolveSiteOnlyEnv` has
 * already worked that out. Going via `/` would resolve the *current* team instead, which is the
 * wrong site whenever the url names another one.
 */
function partnerGuard({ params }: { params: { siteId: string } }) {
  const env = useEnvStore.getState().env;
  if (env && !env.team.is_partner) {
    throw redirect({
      to: '/$siteId/$siteEnv',
      params: { siteId: params.siteId, siteEnv: env.environment },
    });
  }
}

// The partner portal itself. Carries a `siteId` but no `siteEnv`, so it resolves the site's
// environment itself (like `/staff` and `/settings`) before the page renders. Under
// `authedChromeRoute` for the sidebar.
export const partnersRoute = createRoute({
  getParentRoute: () => authedChromeRoute,
  path: '/$siteId/partners',
  component: PartnerOverviewRoute,
  beforeLoad: partnerGuard,
  staticData: { title: 'auth.partners' },
});

// The portal is a single page, so `/overview` said nothing `/partners` doesn't. Kept as a redirect
// because the url is in bookmarks and in the partner invite emails.
export const partnersOverviewRedirectRoute = createRoute({
  getParentRoute: () => authedChromeRoute,
  path: '/$siteId/partners/overview',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/partners', params });
  },
});

// The 2FA redirect destination. It has a `siteId` but no `siteEnv`, so it lives outside the
// site-env layout, under `authedChromeRoute` for the sidebar. The session guards exempt any path
// ending `/settings/password`, so the redirect that lands here can't be bounced straight back.
export const settingsPasswordRoute = createRoute({
  getParentRoute: () => authedChromeRoute,
  path: '/$siteId/settings/password',
  component: SettingsPasswordRoute,
  staticData: { title: 'auth.settings_password' },
});
